// AS TABELAS NA FALA DA NYTA (25/09/2026).
//
// Quando a Nyta lista coisas com colunas (casas de show de uma busca, contatos, comparativos),
// ela escreve uma tabela em markdown. O chat mostrava isso cru, "| # | Nome | |---|---| | 1 | ...",
// por dois motivos: nem o react-markdown da web nem o markdown do app desenham tabela, e o modelo
// muitas vezes manda a tabela INTEIRA NUMA LINHA SÓ, com "| |" no lugar da quebra. Aqui as duas
// formas viram a mesma coisa: cabeçalho e linhas, que a web e o app desenham como planilha.

export interface TabelaDaFala { cabecalho: string[]; linhas: string[][] }

const SEPARADOR = /^:?-{3,}:?$/;
const temSeparador = (t: string) => /\|\s*:?-{3,}:?\s*\|/.test(t);

/**
 * Lê uma tabela markdown, na forma normal (uma linha por linha) ou espremida numa linha só.
 *
 * O que decide as colunas é a linha separadora (`|---|---|`): são N células de traço. Depois dela,
 * as células vão de N em N, e o vazio entre dois `|` que sobra entre uma linha e outra é pulado.
 * Célula vazia DENTRO da linha continua valendo, porque a contagem é por N.
 */
export function lerTabela(bloco: string): TabelaDaFala | null {
  const plano = bloco.split('\n').map((l) => l.trim()).filter(Boolean).join(' ');
  if (!plano.startsWith('|') || !temSeparador(plano)) return null;
  const celulas = plano.split('|').map((c) => c.trim());
  // O primeiro e o último vêm vazios (antes do primeiro `|`, depois do último).
  if (celulas[0] === '') celulas.shift();
  if (celulas[celulas.length - 1] === '') celulas.pop();

  const inicioSep = celulas.findIndex((c) => SEPARADOR.test(c));
  if (inicioSep <= 0) return null;
  let fimSep = inicioSep;
  while (fimSep < celulas.length && SEPARADOR.test(celulas[fimSep])) fimSep++;
  const n = fimSep - inicioSep;
  // O cabeçalho são as N células logo antes do separador (pulando o vão entre eles).
  const antes = celulas.slice(0, inicioSep);
  while (antes.length && antes[antes.length - 1] === '') antes.pop();
  const cabecalho = antes.slice(-n);
  if (cabecalho.length !== n || n < 2) return null;

  const resto = celulas.slice(fimSep);
  const linhas: string[][] = [];
  let i = 0;
  while (i < resto.length) {
    if (resto[i] === '' && resto.length - i > n) i++; // o vão entre uma linha e a outra
    const linha = resto.slice(i, i + n);
    if (linha.length < n || linha.every((c) => c === '')) break;
    linhas.push(linha);
    i += n;
  }
  return linhas.length ? { cabecalho, linhas } : null;
}

export type TrechoComTabela = { tipo: 'texto'; texto: string } | { tipo: 'tabela'; tabela: TabelaDaFala };

/** Parte um texto em trechos de texto e tabelas, na ordem. Sem tabela, devolve o texto inteiro. */
export function trechosComTabela(texto: string): TrechoComTabela[] {
  if (!texto.includes('|') || !temSeparador(texto)) return [{ tipo: 'texto', texto }];
  const saida: TrechoComTabela[] = [];
  const linhas = texto.split('\n');
  let textoAtual: string[] = [];
  const fecharTexto = () => {
    const t = textoAtual.join('\n').trim();
    if (t) saida.push({ tipo: 'texto', texto: t });
    textoAtual = [];
  };
  for (let i = 0; i < linhas.length; i++) {
    if (!linhas[i].trim().startsWith('|')) { textoAtual.push(linhas[i]); continue; }
    // Um bloco de linhas começando com `|`, que é a tabela (na forma espremida, uma linha só).
    let j = i;
    while (j < linhas.length && linhas[j].trim().startsWith('|')) j++;
    const bloco = linhas.slice(i, j).join('\n');
    const tabela = lerTabela(bloco);
    if (tabela) {
      fecharTexto();
      saida.push({ tipo: 'tabela', tabela });
    } else {
      textoAtual.push(bloco);
    }
    i = j - 1;
  }
  fecharTexto();
  return saida.length ? saida : [{ tipo: 'texto', texto }];
}

/** Negrito e itálico de markdown saem da célula: na planilha, o texto vale por si. */
export const textoDaCelula = (c: string) => c.replace(/\*\*(.+?)\*\*/g, '$1').replace(/__(.+?)__/g, '$1').replace(/(^|\s)\*(\S.*?)\*(?=\s|$)/g, '$1$2');

/** A célula que é só um link vira link; o resto é texto. */
export const linkDaCelula = (c: string): string | null => {
  const t = textoDaCelula(c).trim();
  const md = /^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)$/.exec(t);
  if (md) return md[2];
  return /^https?:\/\/\S+$/.test(t) ? t : null;
};

/** O endereço, sem o "https://www." e sem o caminho: é o que cabe numa célula. */
export const dominioDoLink = (url: string) => url.replace(/^https?:\/\/(www\.)?/, '').split(/[/?#]/)[0];
